import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { DailyHabitCatalogItem } from '@/api/types';
import { Modal, ModalBody, ModalHeader } from '@/components/common/Modal';
import { FundamentalIcon } from '@/components/common/FundamentalIcon';
import { getHabitName } from '@/hooks/useFundamentals';
import './FundamentalsCatalog.css';

/** Tope del retraso escalonado: una lista larga no debe arrastrar su propia entrada. */
const MAX_STAGGER_STEPS = 8;
const STAGGER_STEP_MS = 40;
const TITLE_ELEMENT_ID = 'fund-habits-title';

export interface DailyHabitsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nombre del fundamental ya resuelto al idioma activo. */
  fundamentalName: string;
  /** Ícono del fundamental (clase `fa-…` o URL). */
  fundamentalIcon?: string;
  /** Color del fundamental, ya con respaldo aplicado. */
  color: string;
  /** Nombre del comportamiento cuando el modal se abrió desde uno; ausente si no. */
  attitudeName?: string;
  habits: DailyHabitCatalogItem[];
  lang: string;
}

export function DailyHabitsModal({
  isOpen,
  onClose,
  fundamentalName,
  fundamentalIcon,
  color,
  attitudeName,
  habits,
  lang,
}: DailyHabitsModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="fund-habits-modal"
      labelledBy={TITLE_ELEMENT_ID}
      maxWidth={560}
    >
      <div className="fund-habits" style={{ '--fund-color': color } as CSSProperties}>
        {/* Dos figuras excluyentes por ancho de pantalla, resueltas en CSS: al
            costado hace falta espacio libre que en móvil no existe, así que ahí
            la persona se recarga desde arriba en vez de asomarse por la izquierda. */}
        <div className="fund-habits-peek">
          <img
            src="/assets/persona-asomada.svg"
            alt=""
            className="fund-habits-peek-img"
            aria-hidden="true"
          />
        </div>
        <div className="fund-habits-lean">
          <img
            src="/assets/persona-torso.svg"
            alt=""
            className="fund-habits-lean-img"
            aria-hidden="true"
          />
        </div>
        <ModalHeader onClose={onClose}>
          <div className="fund-habits-heading">
            <span className="fund-habits-icon">
              <FundamentalIcon icon={fundamentalIcon} imageClassName="fund-icon-img" />
            </span>
            <span className="fund-habits-titles">
              <span className="fund-habits-title" id={TITLE_ELEMENT_ID}>{fundamentalName}</span>
              <span className="fund-habits-subtitle">{attitudeName ?? t('fundamentals.habitsFor')}</span>
            </span>
          </div>
        </ModalHeader>
        <ModalBody>
          <ul className="fund-habits-list">
            {habits.map((habit, index) => (
              <li
                key={habit.id}
                className="fund-habits-item"
                style={{
                  animationDelay: `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_STEP_MS}ms`,
                }}
              >
                <span className="fund-habits-item-icon">
                  <FundamentalIcon
                    icon={habit.image}
                    imageClassName="fund-icon-img fund-icon-img--sm"
                    fallbackIcon="fa-check"
                  />
                </span>
                <span className="fund-habits-item-name">{getHabitName(habit, lang)}</span>
              </li>
            ))}
          </ul>
        </ModalBody>
      </div>
    </Modal>
  );
}
